import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * Format a price in cents using Shopify's money format template string.
 * Handles all 5 Shopify format tokens: {{amount}}, {{amount_no_decimals}},
 * {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}},
 * {{amount_with_apostrophe_separator}}.
 *
 * @param {number} cents - Price in cents (e.g. 6900 = $69.00)
 * @param {string} format - Shopify money format string (e.g. "${{amount}}")
 * @returns {string} Formatted price string
 */
function formatShopifyMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const [whole, fraction] = amount.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const withPeriods = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const withApostrophes = whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

  return format
    .replace('{{amount}}', withCommas + '.' + fraction)
    .replace('{{amount_no_decimals}}', withCommas)
    .replace('{{amount_with_comma_separator}}', withPeriods + ',' + fraction)
    .replace('{{amount_no_decimals_with_comma_separator}}', withPeriods)
    .replace('{{amount_with_apostrophe_separator}}', withApostrophes + '.' + fraction);
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  // ── Optimistic update state ──
  /** @type {Object<string, Object<string, {price:number, compare_at_price:number|null, available:boolean, image:string|null, options:string}>> | null} */
  #variantDataCache = null;

  /** @type {string | null} */
  #moneyFormat = null;

  /** @type {string | null} – Tracks the last optimistically-applied variant ID so the server response can be compared */
  #optimisticVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // On page load (e.g. refresh with ?variant=...): swap slot #1 to the
    // selected variant's preview image. Slots 2..N keep the original product
    // media ordering (SSR-rendered from selected_product.media).
    this.#syncInitialGalleryImage();
  }

  /**
   * Swaps slot #1 (and the first thumbnail) to the initially-selected variant's
   * image. No-ops if the variant has no image or if slot #1 already matches.
   */
  #syncInitialGalleryImage() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset input[data-current-checked="true"]')
    );
    if (!checkedInput) return;
    const data = this.#resolveVariantData(checkedInput);
    if (!data || !data.image) return;
    const section = this.closest('.shopify-section');
    if (!section) return;
    const gallery = section.querySelector('media-gallery');
    if (!gallery) return;

    const firstSlide = gallery.querySelector('slideshow-slide');
    if (firstSlide) {
      firstSlide.querySelectorAll('img').forEach((img) => {
        if (img instanceof HTMLImageElement) {
          const currentBase = img.src.split('?')[0];
          const newBase = data.image.split('?')[0];
          if (currentBase !== newBase) {
            img.src = data.image;
            img.removeAttribute('srcset');
          }
        }
      });
    }

    const firstThumbImg = gallery.querySelector('.slideshow-controls__thumbnail img');
    if (firstThumbImg instanceof HTMLImageElement) {
      const currentBase = firstThumbImg.src.split('?')[0];
      const newBase = data.image.split('?')[0];
      if (currentBase !== newBase) {
        firstThumbImg.src = data.image;
        firstThumbImg.removeAttribute('srcset');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // ── Optimistic update: resolve variant client-side and update image/price/button instantly ──
    const optimisticData = this.#resolveVariantData(selectedOption);
    if (optimisticData) {
      this.#applyOptimisticUpdate(optimisticData);
    }

    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
      optimistic: !!optimisticData,
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  // ──────────────────────────────────────────────
  // Optimistic client-side variant resolution
  // ──────────────────────────────────────────────

  /**
   * Lazily loads and caches the variant data JSON blob.
   * Returns the full map keyed by pet count → variant ID → data.
   * @returns {Object<string, Object<string, {price:number, compare_at_price:number|null, available:boolean, image:string|null, options:string}>> | null}
   */
  #getVariantData() {
    if (this.#variantDataCache) return this.#variantDataCache;

    const section = this.closest('.shopify-section');
    const script = section?.querySelector('script[data-variant-data]');
    if (!script) return null;

    try {
      this.#variantDataCache = JSON.parse(script.textContent || '{}');
      return this.#variantDataCache;
    } catch {
      return null;
    }
  }

  /**
   * Gets the Shopify money format string.
   * @returns {string}
   */
  #getMoneyFormat() {
    if (this.#moneyFormat) return this.#moneyFormat;
    this.#moneyFormat = (typeof Theme !== 'undefined' && Theme.moneyFormat) || '${{amount}}';
    return this.#moneyFormat;
  }

  /**
   * Resolves the variant data for the clicked option from the client-side cache.
   * Returns null if the variant can't be resolved (e.g., not all options selected yet).
   *
   * @param {HTMLElement} selectedOption - The clicked radio input
   * @returns {{ variantId: string, price: number, compareAtPrice: number|null, available: boolean, image: string|null } | null}
   */
  #resolveVariantData(selectedOption) {
    const variantId = selectedOption.dataset.variantId;
    if (!variantId) return null;

    const allData = this.#getVariantData();
    if (!allData) return null;

    // Search across all pet counts for this variant ID
    for (const petKey of Object.keys(allData)) {
      const variants = allData[petKey];
      if (variants && variants[variantId]) {
        const v = variants[variantId];
        return {
          variantId,
          price: v.price,
          compareAtPrice: v.compare_at_price,
          available: v.available,
          image: v.image || null,
        };
      }
    }

    return null;
  }

  /**
   * Applies optimistic updates to the page immediately (no server roundtrip).
   * Updates: variant ID form input, gallery image, mobile preview, price display, button state.
   *
   * @param {{ variantId: string, price: number, compareAtPrice: number|null, available: boolean, image: string|null }} data
   */
  #applyOptimisticUpdate(data) {
    this.#optimisticVariantId = data.variantId;
    const section = this.closest('.shopify-section');
    if (!section) return;

    // 1. Update variant ID hidden input
    const form = section.querySelector('product-form-component');
    const variantInput = form?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = data.variantId;
    }

    // 2. Update gallery image (only if image changed)
    //    Fall back to product featured image if variant has no specific image.
    let imageUrl = data.image;
    if (!imageUrl) {
      try {
        const mapScript = section.querySelector('script[data-pet-products-map]');
        if (mapScript) {
          const productsMap = JSON.parse(mapScript.textContent || '{}');
          // Find the current product's featured image from the map
          for (const entry of Object.values(productsMap)) {
            if (String(entry.productId) === this.dataset.productId) {
              imageUrl = entry.featuredImage || null;
              break;
            }
          }
        }
      } catch { /* ignore */ }
    }

    if (imageUrl) {
      const gallery = section.querySelector('media-gallery');
      if (gallery) {
        // Scope preview swap to slot #1 so slides 2..N keep their original
        // trust-building media the shopper can still swipe to.
        const firstSlide = gallery.querySelector('slideshow-slide');
        if (firstSlide) {
          firstSlide.querySelectorAll('img').forEach((img) => {
            if (img instanceof HTMLImageElement) {
              const currentBase = img.src.split('?')[0];
              const newBase = imageUrl.split('?')[0];
              if (currentBase !== newBase) {
                img.src = imageUrl;
                img.removeAttribute('srcset');
              }
            }
          });
        }

        const firstThumbImg = gallery.querySelector('.slideshow-controls__thumbnail img');
        if (firstThumbImg instanceof HTMLImageElement) {
          const currentBase = firstThumbImg.src.split('?')[0];
          const newBase = imageUrl.split('?')[0];
          if (currentBase !== newBase) {
            firstThumbImg.src = imageUrl;
            firstThumbImg.removeAttribute('srcset');
          }
        }

        // Snap back to slot #1 so a shopper who scrolled to a later slide
        // sees their customization change immediately.
        const firstThumbBtn = gallery.querySelector('.slideshow-controls__thumbnail');
        if (firstThumbBtn instanceof HTMLElement) firstThumbBtn.click();
      }

      const petFlow = section.querySelector('pet-variant-flow');
      if (petFlow && typeof petFlow.updateMobilePreview === 'function') {
        petFlow.updateMobilePreview(imageUrl);
      }
    }

    // 3. Update price display
    this.#updatePriceOptimistic(section, data.price, data.compareAtPrice);

    // 4. Update button state (enable if available, disable if not)
    const addToCartContainer = form?.querySelector('[ref="addToCartButtonContainer"]') ||
      section.querySelector('add-to-cart-component');
    if (addToCartContainer) {
      if (data.available) {
        if (typeof addToCartContainer.enable === 'function') addToCartContainer.enable();
      } else {
        if (typeof addToCartContainer.disable === 'function') addToCartContainer.disable();
      }
    }
  }

  /**
   * Updates the price display elements with formatted prices.
   *
   * @param {Element} section - The shopify-section element
   * @param {number} price - Price in cents
   * @param {number|null} compareAtPrice - Compare-at price in cents, or null
   */
  #updatePriceOptimistic(section, price, compareAtPrice) {
    const format = this.#getMoneyFormat();
    const priceEl = section.querySelector('product-price .price');
    const compareEl = section.querySelector('product-price .compare-at-price');

    if (priceEl) {
      priceEl.textContent = formatShopifyMoney(price, format);
    }

    if (compareEl) {
      if (compareAtPrice && compareAtPrice > price) {
        compareEl.textContent = formatShopifyMoney(compareAtPrice, format);
        compareEl.closest('[role="group"]')?.removeAttribute('hidden');
      } else {
        compareEl.textContent = '';
        // If no compare price, hide the compare-at group
        const group = compareEl.closest('[role="group"]');
        if (group) group.setAttribute('hidden', '');
      }
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
